import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Check } from 'lucide-react'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'

import { JOURNEY_STEPS } from '@/constants/landing'

const LEARNING_PERKS: string[] = ["No travel", "Revisit any lesson", "Affordable modules"]

const JourneySection: FC = (): ReactNode => {
    return (
        <section id='how-it-works' aria-labelledby='journey-title' className='bg-card py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='journey-title'
                    eyebrow="How it works"
                    title="Your path from learning to earning"
                    description="Skillkafe is for graduates, undergraduates, diploma holders and even school dropouts who want a corporate job and need the right skills to get there."
                />

                <div className='mt-14 grid items-stretch gap-10 lg:grid-cols-12 lg:gap-14'>
                    <div className='reveal relative min-h-[26rem] overflow-hidden rounded-[2rem] bg-sand lg:col-span-5 lg:min-h-0'>
                        <Image
                            src='/images/learner-campus.png'
                            alt="Young man holding course books in a computer lab"
                            fill
                            sizes='(min-width: 1024px) 40vw, 100vw'
                            className='object-cover object-[50%_20%]'
                        />

                        <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-brand-navy/45 via-transparent to-transparent' />

                        <div className='absolute inset-x-4 bottom-4 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur sm:inset-x-6 sm:bottom-6 sm:p-5'>
                            <p className='text-sm font-semibold text-foreground'>
                                Learn online, on your own schedule
                            </p>

                            <ul className='mt-3 flex flex-wrap gap-2'>
                                {LEARNING_PERKS.map(perk => (
                                    <li key={perk} className='inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-brand-teal-ink'>
                                        <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                        {perk}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <ol className='flex flex-col gap-4 lg:col-span-7'>
                        {JOURNEY_STEPS.map((step, index) => {
                            const Icon = step.icon

                            return (
                                <li
                                    key={step.title}
                                    className='reveal group relative flex gap-5 rounded-[1.75rem] border border-border bg-background p-6 transition-colors hover:border-primary/30 sm:gap-6 sm:p-7'
                                >
                                    <span className='relative grid size-14 shrink-0 place-items-center rounded-2xl bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground'>
                                        <Icon className='size-6' aria-hidden='true' />

                                        <span className='absolute -right-2 -top-2 grid size-6 place-items-center rounded-full bg-brand-orange font-heading text-xs font-bold text-brand-navy ring-4 ring-background'>
                                            {index + 1}
                                        </span>
                                    </span>

                                    <div className='pt-1'>
                                        <h3 className='text-lg font-semibold text-foreground sm:text-xl'>
                                            {step.title}
                                        </h3>

                                        <p className='mt-2 leading-relaxed text-muted-foreground'>
                                            {step.description}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </Container>
        </section>
    )
}

export default JourneySection
